import { FieldInstance, FormGroup as FormGroupDef, FormInstance, FormTemplate, Project, UserForResolver } from "db/definitions";
import React, { useCallback, useMemo, useState } from "react";
import FormGroup from "components/FormGroup/FormGroup";
import { ProjectContext } from "utils/ProjectContext";
import { Location } from "./useDrawer";
import { ProgressMetadata } from "@factorearth/component-library";

// Might be able to store project level and site level data within this hook, since they exist across tabs

/**
 * Hook to manage the tabs.
 */
function useTabs(
	createNewInstance: (
		template: FormTemplate,
		formGroup: FormGroupDef,
		linkedAltIds: Record<string, string>,
		linkedData: Record<string, FieldInstance>,
	) => Promise<FormInstance | void>,
	getSingleFormGroup: (id: string) => Promise<FormGroupDef | void>,
	online: boolean,
	userDoc: UserForResolver,
	project: Project | null,
	setupDrawer: (location: Location | "") => void,
	setProgress: (newProgress: Partial<ProgressMetadata>) => void,
) {
	const [tabs, setTabs] = useState<React.JSX.Element[]>([]);

	function handleClearAllTabs() {
		setTabs([]);
	}

	const addNewTab = useCallback(
		/**
		 * Adds the form group in with jsut the id so the form group will retrieve the form group data.
		 * @param formGroupData containing id and name of the form group
		 * @returns final tabs
		 */
		(formGroupData: Pick<FormGroupDef, "id" | "name"> & Partial<FormGroupDef>) => {
			const final = [...tabs];
			// check the tabs to make sure there isnt a duplicate
			for (let i = 0; i < tabs.length; i++) {
				const tab = tabs[i];
				const tabId = tab.props.name;
				if (formGroupData.id === tabId) {
					// its already open
					return final;
				}
			}

			const formGroupHtml = (
				// We need the names here for how we show them in the tab itself. it's a weird way of doing it
				// @ts-ignore
				<ProjectContext.Consumer name={formGroupData.id} formgroupname={formGroupData.name}>
					{({ projectUpdateObj }) => (
						<FormGroup
							createNewInstance={createNewInstance}
							formGroupId={formGroupData.id}
							getSingleFormGroup={getSingleFormGroup}
							online={online}
							project={project}
							setupDrawer={setupDrawer}
							key={formGroupData.id}
							// projectUpdateObj={projectUpdateObj[formGroupData.id]}
							userDoc={userDoc}
							setProgress={setProgress}
						/>
					)}
				</ProjectContext.Consumer>
			);
			final.push(formGroupHtml);

			setTabs(final);
			// FOR NOW JUST DOING THIS to easily view the result, tabs will be completed next
			return final;
		},
		[tabs, online, userDoc, project],
	);

	function removeTab(key: number) {
		const tabCopy = [...tabs].filter((_tab, index) => index !== key - 1);
		setTabs([...tabCopy]);
	}

	function removeMultipleTabs(ids: string[]) {
		const idsToRemove = new Set(ids);
		const tabCopy = [...tabs];
		const filteredTabs = tabCopy.filter((tab) => !idsToRemove.has(tab.props.name));
		setTabs(filteredTabs);
	}

	const handleRefreshTabs = useCallback(() => {
		const idsToRefresh = [];
		for (let i = 0; i < tabs.length; i++) {
			const tab = tabs[i];
			const idObj = {
				id: tab.props.name,
				name: tab.props.formgroupname,
			};

			idsToRefresh.push(idObj);
		}

		setTabs([]);
		const newTabs = [];

		for (let i = 0; i < idsToRefresh.length; i++) {
			const idObj = idsToRefresh[i];
			const formGroupHtml = (
				// We need the names here for how we show them in the tab itself. it's a weird way of doing it
				// @ts-ignore
				<ProjectContext.Consumer name={idObj.id} formgroupname={idObj.name}>
					{({ projectUpdateObj }) => (
						<FormGroup
							setupDrawer={setupDrawer}
							key={idObj.id}
							// projectUpdateObj={projectUpdateObj[formGroupData.id]}
							formGroupId={idObj.id}
							getSingleFormGroup={getSingleFormGroup}
							createNewInstance={createNewInstance}
							online={online}
							userDoc={userDoc}
							project={project}
							setProgress={setProgress}
						/>
					)}
				</ProjectContext.Consumer>
			);

			newTabs.push(formGroupHtml);
		}
		setTabs(newTabs);
	}, [tabs]);

	const results = useMemo(
		() => ({
			tabs,
			removeTab,
			addNewTab,
			handleClearAllTabs,
			handleRefreshTabs,
			removeMultipleTabs,
		}),
		[tabs, removeTab, addNewTab, handleClearAllTabs, handleRefreshTabs, removeMultipleTabs],
	);

	return results;
}

export default useTabs;
